"use client";

import { useState, type FormEvent } from "react";
import {
  ArrowRight,
  Check,
  CircleCheckBig,
  Copy,
  ExternalLink,
  Info,
  MinusCircle,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GoogleMapsHint } from "@/components/google-maps-hint";
import Turnstile from "@/components/turnstile";

interface Decision {
  field: string;
  status: "included" | "omitted" | "fallback";
  reason: string;
}

interface GenerateResponse {
  place: {
    name: string | null;
    address: string | null;
    placeId: string | null;
    website: string | null;
    /** Present when Google returned address components — used to deep-link into /new. */
    city: string | null;
    country: string | null;
    countryCode: string | null;
  };
  script: string;
  decisions: Decision[];
}

type ErrorCode =
  | "INVALID_INPUT"
  | "NOT_FOUND"
  | "NOT_RESTAURANT"
  | "RATE_LIMITED"
  | "CAPTCHA_FAILED"
  | "INTERNAL";

interface ApiError {
  message: string;
  code?: ErrorCode;
  detail?: { placeName?: string | null; primaryType?: string | null };
}

const ADD_BUSINESS_URL =
  "https://support.google.com/business/answer/2911778?co=GENIE.Platform%3DDesktop&hl=en";

export function SchemaGeneratorForm() {
  // Dedicated site key for the /tools/* captcha — kept separate from the
  // site-wide auth key so this page can be captcha-gated without turning
  // on captcha for signup / login (and vice versa).
  const toolsSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_TOOLS_SITE_KEY;
  const turnstileEnabled = !!toolsSiteKey;

  const [mapsLink, setMapsLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<GenerateResponse | null>(null);

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);

  const canSubmit =
    mapsLink.trim().length > 0 &&
    !loading &&
    (!turnstileEnabled || captchaToken !== null || captchaFailed);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    setError(null);
    setResult(null);
    setLoading(true);

    try {
      const response = await fetch("/api/tools/schema/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          placeUrl: mapsLink.trim(),
          captchaToken,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError({
          message: data.error || "Something went wrong",
          code: data.code,
          detail: data.detail,
        });
        return;
      }

      setResult(data as GenerateResponse);
    } catch {
      setError({ message: "Network error. Check your connection and try again." });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setError(null);
    setMapsLink("");
  };

  if (result) {
    return (
      <ResultPanel
        result={result}
        submittedUrl={mapsLink.trim()}
        onReset={resetForm}
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-[var(--radius-xl)] border border-[var(--color-border-default)] bg-[var(--color-bg-card)] p-[var(--space-6)] sm:p-[var(--space-8)] space-y-[var(--space-5)]"
    >
      <div className="space-y-[var(--space-3)]">
        <Input
          label="Google Maps URL"
          placeholder="https://maps.app.goo.gl/..."
          value={mapsLink}
          onChange={(e) => setMapsLink(e.target.value)}
          disabled={loading}
          inputMode="url"
          autoFocus
          aria-invalid={error ? true : undefined}
        />
        <GoogleMapsHint />
      </div>

      {error && <ErrorPanel error={error} />}

      {turnstileEnabled && !captchaFailed && (
        <Turnstile
          sitekey={toolsSiteKey}
          onToken={(token) => {
            setCaptchaToken(token);
            setCaptchaFailed(false);
          }}
          onExpired={() => setCaptchaToken(null)}
          onError={() => setCaptchaFailed(true)}
        />
      )}

      <Button
        type="submit"
        size="lg"
        loading={loading}
        disabled={!canSubmit}
        className="w-full"
      >
        <span className="flex items-center gap-[var(--space-2)]">
          {loading ? "Generating snippet..." : "Generate JSON-LD"}
          {!loading && <ArrowRight className="size-4" aria-hidden="true" />}
        </span>
      </Button>

      <p className="text-center text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
        Restaurants, cafés, bars, and bakeries only. We don&apos;t store
        your site URL or the generated snippet.
      </p>
    </form>
  );
}

function ResultPanel({
  result,
  submittedUrl,
  onReset,
}: {
  result: GenerateResponse;
  /** The Google Maps URL the user typed — passed as `?link=` fallback. */
  submittedUrl: string;
  onReset: () => void;
}) {
  // Build the /new deep link. The /new page accepts the full resolved
  // tuple (placeId + name + city + country) and skips the "paste a URL"
  // step entirely — the user lands on the confirmation panel. Falls back
  // to passing the raw URL as `?link=` when we can't form a complete
  // tuple (rare — only if Google didn't return address components).
  const fullCheckHref = (() => {
    const p = result.place;
    const params = new URLSearchParams();
    if (p.placeId && p.name && p.city && p.country) {
      params.set("placeId", p.placeId);
      params.set("name", p.name);
      params.set("city", p.city);
      params.set("country", p.country);
    } else if (submittedUrl) {
      params.set("link", submittedUrl);
    } else if (p.placeId) {
      // Minimal fallback — /new will treat a bare placeId as needing resolution.
      params.set("placeId", p.placeId);
    }
    const qs = params.toString();
    return qs ? `/new?${qs}` : "/new";
  })();

  return (
    <div className="space-y-[var(--space-6)]">
      {/* Place summary */}
      <div className="rounded-[var(--radius-xl)] border border-[var(--color-primary)]/30 bg-[var(--color-primary-subtle)] p-[var(--space-5)] space-y-[var(--space-2)]">
        <p className="text-[length:var(--font-size-xs)] font-[number:var(--font-weight-semibold)] uppercase tracking-[var(--letter-spacing-wide)] text-[var(--color-primary)]">
          Snippet ready for
        </p>
        <p className="text-[length:var(--font-size-lg)] font-[number:var(--font-weight-bold)] text-[var(--color-text-primary)]">
          {result.place.name || "Your restaurant"}
        </p>
        {result.place.address && (
          <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
            {result.place.address}
          </p>
        )}
      </div>

      {/* JSON-LD code block */}
      <SchemaCodeBlock script={result.script} />

      {/* Decisions ledger */}
      <DecisionsPanel decisions={result.decisions} />

      {/* How to use */}
      <HowToUsePanel />

      {/* CTA: run a full check */}
      <div className="flex flex-col items-center gap-[var(--space-3)] rounded-[var(--radius-xl)] border border-[var(--color-border-default)] bg-[var(--color-bg-card)] p-[var(--space-6)] text-center">
        <div className="flex size-10 items-center justify-center rounded-full bg-[var(--color-primary-subtle)]">
          <Sparkles className="size-5 text-[var(--color-primary)]" aria-hidden="true" />
        </div>
        <p className="text-[length:var(--font-size-base)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
          Want to know what else to fix?
        </p>
        <p className="max-w-md text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
          A full Rekkd check runs ChatGPT, Gemini, Claude, and Perplexity
          against your restaurant and ranks every gap by impact.
        </p>
        <Button render={<a href={fullCheckHref} />} className="mt-[var(--space-1)]">
          Run a full check on {result.place.name || "this place"}{" "}
          <ArrowRight className="size-4" aria-hidden="true" />
        </Button>
        <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
          No need to paste the URL again — we&apos;ll carry it over.
        </p>
        <button
          type="button"
          onClick={onReset}
          className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:underline"
        >
          Generate for a different place
        </button>
      </div>
    </div>
  );
}

function SchemaCodeBlock({ script }: { script: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (insecure context, iframe) — fall back
      // by selecting the text so the user can copy manually.
      const pre = document.getElementById("schema-code-pre");
      if (pre) {
        const range = document.createRange();
        range.selectNodeContents(pre);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };

  return (
    <div className="rounded-[var(--radius-xl)] border border-[var(--color-border-default)] bg-[var(--color-bg-card)] overflow-hidden">
      <div className="flex items-center justify-between gap-[var(--space-3)] border-b border-[var(--color-border-subtle)] px-[var(--space-4)] py-[var(--space-3)]">
        <div>
          <p className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
            JSON-LD snippet
          </p>
          <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
            Paste inside the <code className="font-mono">&lt;head&gt;</code> of every page
          </p>
        </div>
        <Button
          type="button"
          onClick={handleCopy}
          variant={copied ? "primary" : "secondary"}
          size="sm"
        >
          {copied ? (
            <>
              <Check className="size-3.5" aria-hidden="true" /> Copied
            </>
          ) : (
            <>
              <Copy className="size-3.5" aria-hidden="true" /> Copy snippet
            </>
          )}
        </Button>
      </div>
      <pre
        id="schema-code-pre"
        className="max-h-96 overflow-auto bg-[var(--color-bg-muted)] p-[var(--space-4)] text-[length:var(--font-size-xs)] leading-[var(--line-height-relaxed)] text-[var(--color-text-primary)] font-mono"
      >
        <code>{script}</code>
      </pre>
    </div>
  );
}

function DecisionsPanel({ decisions }: { decisions: Decision[] }) {
  const included = decisions.filter((d) => d.status === "included").length;
  const fallback = decisions.filter((d) => d.status === "fallback").length;
  const omitted = decisions.filter((d) => d.status === "omitted").length;

  return (
    <div className="rounded-[var(--radius-xl)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-card)] p-[var(--space-5)] space-y-[var(--space-4)]">
      <div className="flex flex-wrap items-center justify-between gap-[var(--space-2)]">
        <h3 className="font-heading text-[length:var(--font-size-base)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
          What&apos;s in your snippet
        </h3>
        <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
          {included} included
          {fallback > 0 && ` · ${fallback} fallback`}
          {omitted > 0 && ` · ${omitted} omitted`}
        </p>
      </div>

      <ul className="space-y-[var(--space-2)]">
        {decisions.map((d) => (
          <li
            key={d.field}
            className="flex items-start gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-muted)]/40 px-[var(--space-3)] py-[var(--space-2)]"
          >
            <DecisionIcon status={d.status} />
            <div className="flex-1 min-w-0">
              <p className="text-[length:var(--font-size-sm)] font-mono font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]">
                {d.field}
              </p>
              <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
                {d.reason}
              </p>
            </div>
            <StatusBadge status={d.status} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function DecisionIcon({ status }: { status: Decision["status"] }) {
  if (status === "included") {
    return (
      <CircleCheckBig
        className="size-4 shrink-0 mt-[var(--space-0-5)] text-[var(--color-success)]"
        aria-hidden="true"
      />
    );
  }
  if (status === "fallback") {
    return (
      <Info
        className="size-4 shrink-0 mt-[var(--space-0-5)] text-[var(--color-warning)]"
        aria-hidden="true"
      />
    );
  }
  return (
    <MinusCircle
      className="size-4 shrink-0 mt-[var(--space-0-5)] text-[var(--color-text-muted)]"
      aria-hidden="true"
    />
  );
}

function StatusBadge({ status }: { status: Decision["status"] }) {
  const label =
    status === "included" ? "Included" : status === "fallback" ? "Fallback" : "Omitted";
  const cls =
    status === "included"
      ? "bg-[var(--color-success-subtle)] text-[var(--color-success)]"
      : status === "fallback"
        ? "bg-[var(--color-warning-subtle)] text-[var(--color-warning)]"
        : "bg-[var(--color-bg-muted)] text-[var(--color-text-muted)]";
  return (
    <span
      className={`shrink-0 rounded-full px-[var(--space-2)] py-[var(--space-0-5)] text-[length:var(--font-size-xs)] font-[number:var(--font-weight-medium)] ${cls}`}
    >
      {label}
    </span>
  );
}

function HowToUsePanel() {
  return (
    <div className="rounded-[var(--radius-xl)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-card)] p-[var(--space-5)] space-y-[var(--space-3)]">
      <h3 className="font-heading text-[length:var(--font-size-base)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
        How to use this snippet
      </h3>
      <ol className="space-y-[var(--space-2)] text-[length:var(--font-size-sm)] text-[var(--color-text-secondary)]">
        <li className="flex gap-[var(--space-2)]">
          <span className="shrink-0 font-[number:var(--font-weight-semibold)] text-[var(--color-primary)]">
            1.
          </span>
          <span>
            Paste the snippet inside the{" "}
            <code className="font-mono text-[length:var(--font-size-xs)]">&lt;head&gt;</code>{" "}
            of every page on your site — most CMS themes have a &ldquo;head
            scripts&rdquo; field you can drop it into.
          </span>
        </li>
        <li className="flex gap-[var(--space-2)]">
          <span className="shrink-0 font-[number:var(--font-weight-semibold)] text-[var(--color-primary)]">
            2.
          </span>
          <span>
            Validate with Google&apos;s Rich Results Test to confirm it
            parses:{" "}
            <a
              href="https://search.google.com/test/rich-results"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-[var(--space-1)] text-[var(--color-text-link)] hover:underline"
            >
              search.google.com/test/rich-results{" "}
              <ExternalLink className="size-3" aria-hidden="true" />
            </a>
          </span>
        </li>
        <li className="flex gap-[var(--space-2)]">
          <span className="shrink-0 font-[number:var(--font-weight-semibold)] text-[var(--color-primary)]">
            3.
          </span>
          <span>
            Keep your Google Business Profile up to date — if your hours or
            address change, re-run this tool to regenerate.
          </span>
        </li>
      </ol>
    </div>
  );
}

function ErrorPanel({ error }: { error: ApiError }) {
  const isNotFound = error.code === "NOT_FOUND";
  const isNotRestaurant = error.code === "NOT_RESTAURANT";
  const isRateLimited = error.code === "RATE_LIMITED";
  const isCaptcha = error.code === "CAPTCHA_FAILED";

  return (
    <div
      role="alert"
      className="rounded-[var(--radius-lg)] bg-[var(--color-error-subtle)] border border-[var(--color-error)]/20 px-[var(--space-4)] py-[var(--space-3)] flex gap-[var(--space-3)] text-[length:var(--font-size-sm)] text-[var(--color-error)]"
    >
      <AlertCircle className="size-4 shrink-0 mt-[var(--space-0-5)]" aria-hidden="true" />
      <div className="space-y-[var(--space-1)]">
        {isNotFound ? (
          <>
            <p className="font-[number:var(--font-weight-semibold)]">
              Couldn&apos;t find this listing on Google Maps.
            </p>
            <p>
              Double-check the URL, or{" "}
              <a
                href={ADD_BUSINESS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[var(--space-1)] underline font-[number:var(--font-weight-medium)]"
              >
                add your business to Google Maps{" "}
                <ExternalLink className="size-3" aria-hidden="true" />
              </a>{" "}
              first.
            </p>
          </>
        ) : isNotRestaurant ? (
          <>
            <p className="font-[number:var(--font-weight-semibold)]">
              {`${error.detail?.placeName || "This place"} isn't a food business.`}
            </p>
            <p>
              {error.detail?.primaryType
                ? `Google classifies it as "${error.detail.primaryType.replace(/_/g, " ")}". `
                : ""}
              This tool only generates schema for restaurants, cafés, bars,
              and bakeries.
            </p>
          </>
        ) : isRateLimited ? (
          <p>
            Too many requests from your network. Try again in a minute.
          </p>
        ) : isCaptcha ? (
          <p>
            Captcha verification failed. Refresh the page and try again.
          </p>
        ) : (
          <p>{error.message}</p>
        )}
      </div>
    </div>
  );
}
